// 注文・決済の API (#1138): 決済実行(mock/Stripe)/self-order/卓会計/現金/伝票QR/
// 注文チャット。配置ルールは index.ts のヘッダ参照。
import { ApiError, apiFetch } from './_client'
import type { RegisterPayMethod } from '../../lib/storePayment'
import { getDeviceId } from '../../lib/deviceId'
import { markGuestOrdered } from '../../lib/guestId'
import type { OrderItemPayload } from '../../lib/cartPayload'
import type { VariationSnapshot } from '../../lib/menuVariation'

/** #1800 ポイント精算の共通レスポンス (自分の分 / 卓 / テイクアウト / 出前) */
export interface PointsCheckoutResult {
  paid?: string[]
  transactionId: string
  totalAmount: number
  newBalance: number
  campaignReward?: { campaignId: string; amountJpy: number } | null
  /** #1535 この支払いに含まれない店側起票の未払い額 (自分の分のときだけ) */
  excludedStoreEnteredTotal?: number
}

/** #1589 1 回の会計 (決済) の照合サマリ。**手数料・純売上は含まない**
 *  (経営情報は取引履歴 = owner|manager 限定のまま)。 */
export interface Settlement {
  id: string
  orderIds: string[]
  tableNumber?: string | null
  /** レシートに刷られる額。値引き・お通し込みの実請求額。 */
  amountJpyc: number
  discountTotal?: number | null
  discountName?: string | null
  extraItems?: { name: string; price: number; quantity: number }[] | null
  paymentMethod?: string | null
  /** 返金は台帳にしか無い (注文側に焼き込むと返金後に古い額が残る)。 */
  refundAmount?: number | null
  createdAt?: string | null
}

export const ordersApi = {
  // Payment (mock mode — deducts Firestore balance)
  executePayment: async (storeId: string, amount: number, userId: string) => {
    const res = await apiFetch('/payment/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store_id: storeId, amount_jpyc: amount }),
    }, userId)
    return res.json()
  },

  // Payment record (on-chain mode — balance already deducted on-chain)
  recordPayment: async (storeId: string, amount: number, userId: string, txHash: string) => {
    const res = await apiFetch('/payment/record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store_id: storeId, amount_jpyc: amount, tx_hash: txHash }),
    }, userId)
    return res.json()
  },

  // Stripe
  createStripePaymentIntent: async (
    storeId: string,
    amountJpy: number,
    userId: string,
    webauthnAssertionId?: string,  // #734
    // #1038 この「支払い試行」の id。retry で同じ値を送ると Stripe 側で
    // PaymentIntent が二重生成されない。**試行が変わったら必ず振り直すこと**
    // (同じ額をもう一度払う操作まで畳むと客の 2 回目が課金されない)。
    attemptId?: string,
  ) => {
    const res = await apiFetch('/stripe/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        store_id: storeId,
        amount_jpy: amountJpy,
        webauthn_assertion_id: webauthnAssertionId,
        // #1227: 決済端末の識別子。PAYMENT_REQUIRES_DEVICE_TRUST=ON のとき、
        // 信頼済み端末からのみ決済を許可する判定に使う (未信頼は DEVICE_NOT_TRUSTED)。
        device_id: getDeviceId(),
        attempt_id: attemptId,
      }),
    }, userId)
    return res.json()
  },

  confirmStripePayment: async (paymentIntentId: string, storeId: string, amountJpy: number, userId: string) => {
    const res = await apiFetch('/stripe/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payment_intent_id: paymentIntentId, store_id: storeId, amount_jpy: amountJpy }),
    }, userId)
    return res.json()
  },

  // #625: charge 前にカード単位上限 (PAYMENT_LIMIT_CARD_DAILY / _RATE) をチェック。
  // フロントは createPaymentMethod() で PM を先に作って (charge せず) 本 endpoint を
  // 叩き、200 が返ってから confirmPayment で実 charge する。違反時は 400 + code を
  // 投げ、フロントは ApiError.code を見てユーザーへエラー表示し confirmPayment を
  // 呼ばない (= 手数料発生ゼロ)。
  precheckStripePayment: async (
    paymentIntentId: string,
    storeId: string,
    amountJpy: number,
    paymentMethodId: string,
    userId: string,
  ) => {
    const res = await apiFetch('/stripe/precheck-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        store_id: storeId,
        amount_jpy: amountJpy,
        payment_method_id: paymentMethodId,
      }),
    }, userId)
    return res.json()
  },

  // Self-order (#289) — deferred-payment model.
  // Customer creates an order without paying; settles all unpaid orders at
  // checkout time via createSelfOrderCheckout + confirmSelfOrderCheckout.
  // Gated by VITE_SELF_ORDER_ENABLED (UI) + SELF_ORDER_ENABLED (API).
  // #1641 同じ卓の「自分以外」の未払い注文。以前は Firestore を直接購読して卓の
  // 全注文を読んでいた (公開読み取りだったため)。着席している人だけが取れる。
  listTableOrders: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch(
      `/orders/table-orders?storeId=${encodeURIComponent(storeId)}&tableNumber=${encodeURIComponent(tableNumber)}`,
    )
    return res.json() as Promise<{
      storeId: string
      tableNumber: string
      orders: {
        id: string
        storeId: string
        tableNumber: string | null
        totalAmount: number
        status: string
        paid: boolean
        orderType?: string | null
        callNumber?: string | null
        placedBy: 'staff' | 'customer'
        createdAt?: string | null
        items: { name: string; quantity: number; variations?: VariationSnapshot[] }[]
      }[]
    }>
  },

  createSelfOrder: async (
    storeId: string,
    items: OrderItemPayload[],
    /** #1398 ゲストは持たない。省略すると apiFetch が X-Guest-Id で通す。 */
    userId: string | undefined,
    opts?: {
      tableNumber?: string
      notes?: string
      // #284 出前: when orderType is 'delivery' the API expects
      // deliveryAddress and uses the store's delivery rules.
      // #1213 'takeout' は住所も卓も不要 (先払い + 呼び出し番号で受け取り)
      orderType?: 'dine-in' | 'delivery' | 'takeout'
      deliveryAddress?: string
      deliveryNotes?: string
      deliveryLat?: number
      deliveryLng?: number
    },
  ) => {
    const res = await apiFetch('/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storeId,
        items,
        tableNumber: opts?.tableNumber,
        notes: opts?.notes,
        orderType: opts?.orderType,
        deliveryAddress: opts?.deliveryAddress,
        deliveryNotes: opts?.deliveryNotes,
        deliveryLat: opts?.deliveryLat,
        deliveryLng: opts?.deliveryLng,
      }),
    }, userId)
    // #2140 ゲストが注文した印 (「できました」の通知を、注文したゲストにだけ出すため)
    if (!userId) markGuestOrdered()
    return res.json()
  },

  // #284 出前 single-order pre-payment. Dine-in bundles at end-of-meal via
  // createSelfOrderCheckout; delivery must pay upfront for this one order.
  createDeliveryCheckout: async (orderId: string, userId: string) => {
    const res = await apiFetch('/orders/delivery/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    }, userId)
    return res.json()
  },

  // #1800 YOKOSO ポイントで精算する。カードと違い confirm 段は無く、成功した時点で
  // 注文は paid。残高不足分をその場でチャージして払うときだけ topup を渡す (#1190)。
  // 店が受けない / KYC 未完了 / 上限超過は ApiError (code 付き) で返る →
  // lib/apiErrorMessage.ts の paymentDeclineI18nKey で文言に引く。
  // #2155 expectedTotal = お客様に見せた額。サーバの額と違えば 409 amount_changed (読み直す)
  createSelfOrderPointsCheckout: async (
    storeId: string, userId: string, topup?: { topupAmount: number; topupFeeJpyc: number },
    expectedTotal?: number,
  ) => {
    const res = await apiFetch('/orders/points-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, ...(topup || {}), expectedTotal }),
    }, userId)
    return res.json() as Promise<PointsCheckoutResult>
  },

  createTablePointsCheckout: async (
    storeId: string, tableNumber: string, userId: string,
    topup?: { topupAmount: number; topupFeeJpyc: number },
    expectedTotal?: number,
  ) => {
    const res = await apiFetch('/orders/table-points-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber, ...(topup || {}), expectedTotal }),
    }, userId)
    return res.json() as Promise<PointsCheckoutResult>
  },

  createDeliveryPointsCheckout: async (
    orderId: string, userId: string, topup?: { topupAmount: number; topupFeeJpyc: number },
  ) => {
    const res = await apiFetch('/orders/delivery/points-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId, ...(topup || {}) }),
    }, userId)
    return res.json() as Promise<PointsCheckoutResult>
  },

  // #1800 カウンター受け取りをポイントで先払い → その場で注文が作られる (下書き無し)
  takeoutPointsCheckout: async (
    storeId: string,
    items: OrderItemPayload[],
    notes?: string,
    diningLocation?: 'eat_in' | 'takeout',
    topup?: { topupAmount: number; topupFeeJpyc: number },
  ) => {
    const res = await apiFetch('/orders/takeout/points-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, items, notes, diningLocation, ...(topup || {}) }),
    })
    return res.json() as Promise<PointsCheckoutResult & { order: { id: string; callNumber?: string } }>
  },

  createSelfOrderCheckout: async (storeId: string, userId: string) => {
    const res = await apiFetch('/orders/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId }),
    }, userId)
    return res.json()
  },

  // #941 現金会計: flag the customer's unpaid dine-in orders as cash-pending.
  // No Stripe call — the customer pays at the register and the store confirms
  // receipt via approveCashPayment. Returns { orderIds, totalAmount, storeName }.
  /** #1398 ゲストも使える (金銭は動かず「レジで払う」と申告するだけ)。 */
  createSelfOrderCashCheckout: async (storeId: string, userId?: string) => {
    const res = await apiFetch('/orders/cash-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId }),
    }, userId)
    return res.json()
  },

  // #942 店舗側現金受領: owner-only. Confirms cash received for a customer's
  // bill → flips those orders paid=true + records a cash transaction.
  // #1866 cashReceived は**任意**。渡すとレシートに「お預り / お釣り」が出る。
  // #2155 expectedTotal = 店員が確認画面で見た額。サーバの額と違えば 409 amount_changed
  approveCashPayment: async (
    storeId: string, customerId: string, idToken?: string | null, cashReceived?: number,
    expectedTotal?: number,
    method: RegisterPayMethod = 'cash',  // #2162 現金 / お店の決済端末
  ) => {
    const res = await apiFetch('/orders/approve-cash', {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, customerId, cashReceived, expectedTotal, method }),
    })
    return res.json()
  },

  // #2155 店員の「現金を受領しましたか？」に出す額 = 受領で受け取る額 (卓を締めるならお通し・席料込み)。
  // customerId はその客の分 (approve-cash)、tableNumber は卓まとめ (approve-cash-table)
  getCashPendingBill: async (storeId: string, target: { customerId?: string; tableNumber?: string }) => {
    const qs = new URLSearchParams({ storeId })
    if (target.customerId) qs.set('customerId', target.customerId)
    if (target.tableNumber) qs.set('tableNumber', target.tableNumber)
    const res = await apiFetch(`/orders/cash-pending-bill?${qs.toString()}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load the cash bill')
    return res.json() as Promise<{
      orderIds: string[]
      ordersTotal: number
      // 卓ごとの行。「外す」は行の卓に効かせる
      extras: { presetId: string; name: string; price: number; quantity: number; tableNumber: string }[]
      extrasTotal: number
      totalAmount: number
    }>
  },

  // #977 店員による代理注文。テーブル番号＋メニュー品を渡す。
  // #1392 認証は apiFetch 任せ (staff セッション → owner Firebase の順で自動添付)。
  // 以前は Firebase ID トークンを明示的に渡していたため、**Firebase ユーザーを
  // 持たない staff / manager では呼び出す前に落ちていた**。
  createStaffOrder: async (
    storeId: string,
    tableNumber: string,
    items: OrderItemPayload[],
    notes?: string,
  ) => {
    const res = await apiFetch('/orders/staff-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber, items, notes }),
    })
    return res.json()
  },

  confirmSelfOrderCheckout: async (paymentIntentId: string, orderIds: string[], userId: string) => {
    const res = await apiFetch('/orders/checkout/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId, orderIds }),
    }, userId)
    return res.json()
  },

  // #956 Phase 2 卓単位一括会計: a seated customer (representative) settles every
  // unpaid order at their table — across accounts — in one bill (card or cash).
  // getTableBill previews the combined total so the sheet can offer
  // 「テーブル全員分」alongside「自分の分」.
  /** #1398 ゲストも卓に着いていれば見られる (サーバが着席を検証する)。 */
  getTableBill: async (storeId: string, tableNumber: string, userId?: string) => {
    const res = await apiFetch(
      `/orders/table-bill?storeId=${encodeURIComponent(storeId)}&tableNumber=${encodeURIComponent(tableNumber)}`,
      { method: 'GET' }, userId,
    )
    return res.json()
  },

  createTableCheckout: async (storeId: string, tableNumber: string, userId: string) => {
    const res = await apiFetch('/orders/table-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber }),
    }, userId)
    return res.json()
  },

  confirmTableCheckout: async (
    paymentIntentId: string, orderIds: string[], storeId: string, tableNumber: string, userId: string,
  ) => {
    const res = await apiFetch('/orders/table-checkout/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId, orderIds, storeId, tableNumber }),
    }, userId)
    return res.json()
  },

  /** #1398 ゲストも使える (店頭払いの申請なので金銭は動かない)。 */
  createTableCashCheckout: async (storeId: string, tableNumber: string, userId?: string) => {
    const res = await apiFetch('/orders/table-cash-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber }),
    }, userId)
    return res.json()
  },

  // #956 Phase 2: owner confirms cash for a whole table's representative bill.
  approveCashTable: async (
    storeId: string, tableNumber: string, idToken?: string | null, cashReceived?: number,
    expectedTotal?: number,
    method: RegisterPayMethod = 'cash',  // #2162 現金 / お店の決済端末
  ) => {
    const res = await apiFetch('/orders/approve-cash-table', {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber, cashReceived, expectedTotal, method }),
    })
    return res.json()
  },

  // #1641 認証はヘッダ (ログイン客の Bearer / ゲストの X-Guest-Id) で足りる。
  // **id はクエリに載せない** — ゲスト ID は bearer 相当なので URL に出さない (#1849)。
  listMyOrders: async (userId?: string) => {
    const res = await apiFetch('/orders/me', { method: 'GET' }, userId)
    return res.json()
  },

  /** #1398 ゲストで作った注文をログインしたアカウントへ引き継ぐ。
   *  食事の途中でログインしても会計が割れないようにする。 */
  claimGuestOrders: async (guestId: string) => {
    const res = await apiFetch('/orders/claim-guest-orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId }),
    })
    return res.json() as Promise<{ claimed: string[] }>
  },

  listStoreOrders: async (storeId: string, status?: string) => {
    const qs = status ? `?storeId=${storeId}&status=${status}` : `?storeId=${storeId}`
    const res = await apiFetch(`/orders${qs}`, { method: 'GET' })
    return res.json()
  },

  // #1001 PH3: fetch the human-readable rendering of a kitchen ticket so
  // the owner-facing viewer can show "what the printer would emit". Owner-
  // only server-side; the UI just needs the store owner's Firebase token
  // (which apiFetch attaches automatically).
  getKitchenTicketPreview: async (orderId: string) => {
    const res = await apiFetch(`/orders/${orderId}/kitchen-ticket-preview`, { method: 'GET' })
    return res.json() as Promise<{
      preview: string
      bytes: number
      qrData: string
      orderId: string
      status: string
      /** #1005 D: raw ESC/POS bytes for tablet-side ePOS-Print printing.
       *  Present since 2026-07; may be absent on older API versions. */
      escposBase64?: string
      escposBytes?: number
    }>
  },

  // #1222 厨房伝票を印刷したことをサーバに記録 (印刷成功後に呼ぶ)。冪等。
  // localStorage だけでなくサーバ側 kitchenPrintedAt で二重印刷を防ぐため。
  markKitchenPrinted: async (orderId: string) => {
    const res = await apiFetch(`/orders/${orderId}/kitchen-printed`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to mark kitchen ticket printed')
    return res.json() as Promise<{ ok: boolean; alreadyPrinted: boolean }>
  },

  // #1001 PH3 Step 2: staff scans the receipt QR → verifies the signed token
  // → advances the order to served/delivered (skip-through 対応、"ワンタップ
  // 完結"). `token` = HMAC 12byte b64url printed on the ticket.
  receiptScan: async (
    orderId: string,
    token: string,
    action: 'served' | 'delivered',
  ) => {
    const res = await apiFetch(`/orders/${orderId}/receipt-scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, action }),
    })
    return res.json()
  },

  // #1005 follow-up: 公開(login 不要)完結。URL 型の伝票 QR をスタッフがスマホ標準
  // カメラで開く → 署名 token だけで提供/配送完了。/complete ページから呼ぶ。
  completeByToken: async (
    orderId: string,
    token: string,
  ): Promise<{ status: string; orderType: string; storeName?: string }> => {
    const res = await apiFetch(`/orders/${orderId}/complete-by-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
    return res.json()
  },

  updateOrderStatus: async (
    orderId: string,
    // #1215 takeout: 'ready' (呼び出し) / 'picked_up' (受け渡し完了)
    status: 'preparing' | 'served' | 'ready' | 'picked_up' | 'out_for_delivery' | 'delivered' | 'cancelled',
    idToken?: string | null,
  ) => {
    const res = await apiFetch(`/orders/${orderId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ status }),
    })
    return res.json()
  },

  // #314 task #5: customer flags an order as not actually delivered.
  reportOrderMissing: async (orderId: string, userId: string) => {
    const res = await apiFetch(`/orders/${orderId}/report-missing`, {
      method: 'POST',
    }, userId)
    return res.json()
  },

  // #338 in-order chat — customer-side send + read reset
  sendOrderMessage: async (orderId: string, body: string, userId: string) => {
    const res = await apiFetch(`/orders/${orderId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }, userId)
    return res.json()
  },
  markOrderMessagesRead: async (orderId: string, userId: string) => {
    const res = await apiFetch(`/orders/${orderId}/messages/read`, {
      method: 'POST',
    }, userId)
    return res.json()
  },

  // #338 in-order chat — owner-side send + read reset
  sendOwnerOrderMessage: async (orderId: string, body: string, idToken: string) => {
    const res = await apiFetch(`/orders/${orderId}/owner-messages`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    })
    return res.json()
  },
  markOwnerOrderMessagesRead: async (orderId: string, idToken: string) => {
    const res = await apiFetch(`/orders/${orderId}/owner-messages/read`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1025 チャットの読み出し。以前は client の onSnapshot が
  // orders/{id}/messages を直読していたが、rules が公開だったため全店の
  // チャット本文が読めた。読み出しを API に寄せ、rules は deny にした。
  getOrderMessages: async (orderId: string, userId: string | undefined) => {
    const res = await apiFetch(`/orders/${orderId}/messages`, {}, userId)
    return res.json()
  },
  // 店側は owner / staff どちらも通る。apiFetch が店舗ルートでは staff
  // セッションを、それ以外では Firebase ID トークンを載せる。
  getOwnerOrderMessages: async (orderId: string) => {
    const res = await apiFetch(`/orders/${orderId}/owner-messages`, {})
    return res.json()
  },

  // #1025 出前の届け先 (住所 / 座標 / 受け渡しメモ)。公開 doc から分離したので
  // onSnapshot では取れない。当事者だけが読める。
  getDeliveryDetail: async (orderId: string, userId: string | undefined) => {
    const res = await apiFetch(`/orders/${orderId}/delivery-detail`, {}, userId)
    return res.json()
  },
  getOwnerDeliveryDetail: async (orderId: string) => {
    const res = await apiFetch(`/orders/${orderId}/owner-delivery-detail`, {})
    return res.json()
  },

  // #284: customer-initiated cancel for a delivery order (only allowed
  // while status='received'). Issues a Stripe refund inline if paid.
  cancelMyOrder: async (orderId: string, userId: string) => {
    const res = await apiFetch(`/orders/${orderId}/cancel`, {
      method: 'POST',
    }, userId)
    return res.json()
  },

  // #284: store-initiated cancel + auto-refund for unintelligible address,
  // unreachable customer, or any other reason the owner can't fulfil.
  ownerCancelDelivery: async (orderId: string, idToken: string, reason?: string) => {
    const res = await apiFetch(`/orders/${orderId}/owner-cancel`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || null }),
    })
    return res.json()
  },

  // #314 task #5: owner-only — cancel + clone the missing order so the
  // kitchen makes it again. Returns { cancelled, remade }.
  remakeOrder: async (orderId: string, idToken: string) => {
    const res = await apiFetch(`/orders/${orderId}/remake`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #947 (管理番号_004) owner-only — approve a table's first order (sends it
  // to the kitchen + opens the table) or reject it (no one seated → block a
  // remote/prank order).
  approveOrder: async (orderId: string, idToken?: string | null, partySize?: number) => {
    const res = await apiFetch(`/orders/${orderId}/approve`, {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ partySize }),
    })
    return res.json()
  },
  rejectOrder: async (orderId: string, idToken?: string | null) => {
    const res = await apiFetch(`/orders/${orderId}/reject`, {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
    })
    return res.json()
  },

  // #952 (管理番号_012) owner-only — approve/reject every pending first order
  // for a table in one action (one approval opens the whole table).
  // #1353 allYouCanPartySize: 放題プランの注文が含まれるとき、店員が見た実人数で
  // 開始する（客の申告は希望であって確定値ではない）。
  // #1919 認証は apiFetch 任せ (店舗の画面ではスタッフのセッション → オーナーの
  // Firebase の順で自動添付)。**オーナーのトークンを必須にしない** — スタッフだけの
  // 端末で操作できなくなるため (サーバ側は元から全ロール可)。idToken は互換のため残す。
  approveTable: async (
    storeId: string,
    tableNumber: string,
    idToken?: string | null,
    allYouCanPartySize?: number,
  ) => {
    const res = await apiFetch('/orders/approve-table', {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber, allYouCanPartySize }),
    })
    return res.json()
  },
  rejectTable: async (storeId: string, tableNumber: string, idToken?: string | null) => {
    const res = await apiFetch('/orders/reject-table', {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber }),
    })
    return res.json()
  },

  // #314 task #5: owner-only — cancel the order without remaking.
  voidOrder: async (orderId: string, idToken: string) => {
    const res = await apiFetch(`/orders/${orderId}/void`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1587 明細単位の取り消し。「う巻き玉子だけ売り切れ」で注文ごと却下せずに済む。
  // 全部指定すると注文ごと cancelled になる (空の伝票を残さない)。
  // #1589 会計の照合サマリ (ALL_ROLES)。注文を足した額は**レシートの金額と
  // 一致しない** (値引き・お通しは台帳側) ので、実請求額はここから引く。
  // 取引履歴 API は owner|manager 限定でバイトが読めないため別口。
  listSettlements: async (storeId: string, idToken: string) => {
    const res = await apiFetch(`/orders/settlements?storeId=${encodeURIComponent(storeId)}`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to list settlements')
    return res.json() as Promise<{ settlements: Settlement[] }>
  },

  // #2031 行は鍵で指す (`item.lineKey ?? item.menuItemId`)。バリエーションを入れると同じ商品が
  // 1 つの注文に 2 行入るので、商品 ID で「全行」を指す形は残していない。
  voidOrderItems: async (orderId: string, lineKeys: string[], idToken?: string | null) => {
    const res = await apiFetch(`/orders/${orderId}/void-items`, {
      method: 'POST',
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ lineKeys }),
    })
    return res.json()
  },

  // #314 task #5: owner rejects an incorrect missing-report — leaves the
  // order as served + unpaid (food was actually delivered).
  dismissMissingReport: async (orderId: string, idToken: string) => {
    const res = await apiFetch(`/orders/${orderId}/dismiss-missing`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    return res.json()
  },

  // #1217 カウンター受け取りの先払い。**注文はまだ作らない** — カートを送って
  // PaymentIntent だけ作り、決済が通ってから takeoutConfirm で注文化する。
  // (先に注文を作ると、決済をやめた分が店の伝票・客の一覧に漏れる)
  /** #1402 客がスマホで選んだ注文をレジに引き渡す受付番号を発行する。
   *  注文はまだ作らない — レジで会計が通った時点で注文化される。ゲストも使える。 */
  createCounterRequest: async (
    storeId: string,
    items: OrderItemPayload[],
    opts?: { notes?: string; diningLocation?: 'eat_in' | 'takeout' },
  ) => {
    const res = await apiFetch('/orders/counter-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, items, ...opts }),
    })
    return res.json() as Promise<{
      code: string
      totalAmount: number
      expiresInMinutes: number
      storeName: string
    }>
  },

  /** #1171 diningLocation: 税率は飲食場所で決まる (受け取り方では決まらない)。 */
  takeoutCheckout: async (
    storeId: string,
    items: OrderItemPayload[],
    notes?: string,
    diningLocation?: 'eat_in' | 'takeout',
    // #2561 この「支払いの試み」の id。送り直しで同じ値を送ると決済が 2 つ作られない。**試みが変わったら必ず
    // 振り直すこと** (同じカートをもう一度頼むのは正当。畳むと 2 回目が課金されない)
    attemptId?: string,
  ) => {
    const res = await apiFetch('/orders/takeout/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, items, notes, diningLocation, attemptId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to start takeout checkout')
    return res.json() as Promise<{
      clientSecret: string
      paymentIntentId: string
      connectedAccountId: string
      totalAmount: number
    }>
  },

  // #1217 レジで店員が受け取り注文を作る (客はアプリ不要)。ALL_ROLES。
  // 「決済してから注文」— 会計 (現金/S700) を先に確定してから paid=true で呼ぶ。
  // paymentTransactionId は客のデジタルレシート導線に紐付ける (現金の tx など)。
  // #2031 レジの受け取り注文は**会計が先**。会計の前にサーバが検算して値段を決め、下書きに保存する。
  // 画面は返ってきた totalAmount で会計し、会計後に draftId を付けて createCounterOrder を呼ぶ
  // (サーバは下書きから注文を作るだけ — 会計後に弾かれない・会計額と注文の合計が食い違わない)。
  prepareCounterOrder: async (storeId: string, items: OrderItemPayload[]) => {
    const res = await apiFetch('/orders/counter-order/prepare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, items }),
    })
    return res.json() as Promise<{ draftId: string; totalAmount: number }>
  },

  createCounterOrder: async (
    storeId: string,
    items: OrderItemPayload[],
    notes?: string,
    opts?: { paid?: boolean; paymentTransactionId?: string; draftId?: string },
  ) => {
    const res = await apiFetch('/orders/counter-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        storeId, items, notes,
        paid: opts?.paid ?? false,
        paymentTransactionId: opts?.paymentTransactionId,
        draftId: opts?.draftId,
      }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create counter order')
    return res.json() as Promise<{
      order: { id: string; callNumber?: string }
      // アプリ不要の受け取り状況ページ URL (会計後に客へ QR 提示)
      statusUrl?: string
    }>
  },

  // #1217 店頭注文の会計完了を記録 (現金/S700 の決済は別レールで済ませた後)。
  markCounterOrderPaid: async (orderId: string) => {
    const res = await apiFetch(`/orders/${orderId}/mark-paid`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to mark order paid')
    return res.json() as Promise<{ order: { id: string; paid: boolean } }>
  },

  // #1217 決済確定 → ここで初めて注文が作られ、厨房へ流れる (冪等)。
  takeoutConfirm: async (paymentIntentId: string) => {
    const res = await apiFetch('/orders/takeout/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to confirm takeout order')
    return res.json() as Promise<{ order: { id: string; callNumber?: string } }>
  },

  // #1216 受け取り案内を表示したことをサーバに記録 (端末を跨いで一度きり)。
  ackReadyNotice: async (orderIds: string[]) => {
    const res = await apiFetch('/orders/ready-notice/ack', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderIds }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to ack ready notice')
    return res.json() as Promise<{ acked: string[] }>
  },

  // #1215 客がカウンターで提示する受け取りパス (番号 + 受け渡し完了 QR)。本人のみ。
  getPickupPass: async (orderId: string) => {
    const res = await apiFetch(`/orders/${orderId}/pickup-pass`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get pickup pass')
    return res.json() as Promise<{
      orderId: string
      callNumber: string | null
      status: string
      qrData: string
    }>
  },
}
